/* Full Set · componentes interactivos (carrusel, lookbook, cuenta atrás) */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Carrusel de portada ---------- */
  if (!customElements.get('fs-slider')) {
    class FsSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.fs-slide'));
        this.dots = Array.from(this.querySelectorAll('.fs-slider__dot'));
        this.pauseBtn = this.querySelector('[data-pause]');
        this.index = 0;
        this.speed = parseInt(this.dataset.speed, 10) || 6000;
        this.paused = this.dataset.autoplay !== 'true' || reduceMotion || this.slides.length < 2;
        this.style.setProperty('--fs-slide-speed', `${this.speed}ms`);
        if (this.slides.length < 2) return;

        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.go), true)));
        this.pauseBtn?.addEventListener('click', () => this.setPaused(!this.paused));

        // Pausa al pasar el ratón o al enfocar dentro del carrusel
        this.addEventListener('mouseenter', () => this.hold(true));
        this.addEventListener('mouseleave', () => this.hold(false));
        this.addEventListener('focusin', () => this.hold(true));
        this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.hold(false); });

        // Deslizar en móvil
        let startX = null;
        this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
        this.addEventListener('touchend', (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1), true);
          startX = null;
        });

        // Editor de temas: mostrar la diapositiva seleccionada
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
          if (i > -1) { this.go(i, true); this.hold(true); }
        });
        document.addEventListener('shopify:block:deselect', () => this.hold(false));

        this.setPaused(this.paused);
      }

      disconnectedCallback() { clearTimeout(this.timer); }

      go(i, fromUser = false) {
        const n = this.slides.length;
        const next = (i + n) % n;
        if (next === this.index && !fromUser) return;
        this.slides.forEach((slide, k) => {
          const on = k === next;
          slide.classList.toggle('is-active', on);
          if (on) slide.removeAttribute('aria-hidden'); else slide.setAttribute('aria-hidden', 'true');
          slide.querySelectorAll('a, button').forEach((el) => { if (on) el.removeAttribute('tabindex'); else el.setAttribute('tabindex', '-1'); });
        });
        this.dots.forEach((dot, k) => {
          dot.classList.toggle('is-active', k === next);
          if (k === next) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
        });
        this.index = next;
        this.restartProgress();
        this.schedule();
      }

      restartProgress() {
        const bar = this.dots[this.index]?.querySelector('.fs-slider__dot-bar');
        if (!bar) return;
        bar.style.animation = 'none';
        void bar.offsetWidth;
        bar.style.animation = '';
      }

      schedule() {
        clearTimeout(this.timer);
        if (this.paused || this.held) return;
        this.timer = setTimeout(() => this.go(this.index + 1), this.speed);
      }

      hold(on) {
        this.held = on;
        this.classList.toggle('is-held', on);
        this.schedule();
      }

      setPaused(on) {
        this.paused = on;
        this.classList.toggle('is-paused', on);
        if (this.pauseBtn) {
          this.pauseBtn.setAttribute('aria-pressed', String(on));
          this.pauseBtn.setAttribute('aria-label', on ? 'Reproducir carrusel' : 'Pausar carrusel');
        }
        this.schedule();
      }
    }
    customElements.define('fs-slider', FsSlider);
  }

  /* ---------- Compra el look (puntos clicables) ---------- */
  if (!customElements.get('fs-lookbook')) {
    class FsLookbook extends HTMLElement {
      connectedCallback() {
        this.dots = Array.from(this.querySelectorAll('.fs-hotspot__dot'));
        this.items = Array.from(this.querySelectorAll('.fs-lookbook__item'));
        this.dots.forEach((dot) => {
          dot.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = dot.getAttribute('aria-expanded') === 'true';
            this.closeAll();
            if (!open) this.open(dot);
          });
          dot.addEventListener('mouseenter', () => this.highlight(Number(dot.dataset.index)));
          dot.addEventListener('mouseleave', () => this.highlight(-1));
        });
        this.items.forEach((item) => {
          item.addEventListener('mouseenter', () => this.highlight(Number(item.dataset.index)));
          item.addEventListener('mouseleave', () => this.highlight(-1));
        });
        this.onDocClick = (e) => { if (!this.contains(e.target)) this.closeAll(); };
        this.onKey = (e) => {
          if (e.key !== 'Escape') return;
          const openDot = this.dots.find((d) => d.getAttribute('aria-expanded') === 'true');
          this.closeAll();
          openDot?.focus();
        };
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKey);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
      }

      open(dot) {
        dot.setAttribute('aria-expanded', 'true');
        dot.closest('.fs-hotspot')?.classList.add('is-open');
        const card = document.getElementById(dot.getAttribute('aria-controls'));
        if (card) card.hidden = false;
        this.highlight(Number(dot.dataset.index));
      }

      closeAll() {
        this.dots.forEach((dot) => {
          dot.setAttribute('aria-expanded', 'false');
          dot.closest('.fs-hotspot')?.classList.remove('is-open');
          const card = document.getElementById(dot.getAttribute('aria-controls'));
          if (card) card.hidden = true;
        });
        this.highlight(-1);
      }

      highlight(i) {
        this.items.forEach((item) => item.classList.toggle('is-active', Number(item.dataset.index) === i));
        this.dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.index) === i));
      }
    }
    customElements.define('fs-lookbook', FsLookbook);
  }

  /* ---------- Cuenta atrás ---------- */
  if (!customElements.get('fs-countdown')) {
    class FsCountdown extends HTMLElement {
      connectedCallback() {
        const end = new Date(this.dataset.end);
        if (Number.isNaN(end.getTime())) return;
        this.end = end;
        this.els = {
          d: this.querySelector('[data-d]'),
          h: this.querySelector('[data-h]'),
          m: this.querySelector('[data-m]'),
          s: this.querySelector('[data-s]'),
        };
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() { clearInterval(this.timer); }

      tick() {
        const diff = this.end - Date.now();
        if (diff <= 0) { this.finish(); return; }
        const pad = (n) => String(n).padStart(2, '0');
        const s = Math.floor(diff / 1000);
        this.els.d.textContent = pad(Math.floor(s / 86400));
        this.els.h.textContent = pad(Math.floor((s % 86400) / 3600));
        this.els.m.textContent = pad(Math.floor((s % 3600) / 60));
        this.els.s.textContent = pad(s % 60);
      }

      finish() {
        clearInterval(this.timer);
        const inEditor = window.Shopify && window.Shopify.designMode;
        if (this.dataset.hide === 'true' && !inEditor) {
          const section = this.closest('.shopify-section');
          (section || this).style.display = 'none';
          return;
        }
        this.querySelector('.fs-countdown__timer')?.setAttribute('hidden', '');
        this.querySelector('.fs-countdown__btn')?.setAttribute('hidden', '');
        const done = this.querySelector('.fs-countdown__done');
        if (done) done.hidden = false;
      }
    }
    customElements.define('fs-countdown', FsCountdown);
  }
})();

/* ---------- Barra de compra fija (página de producto) ---------- */
(() => {
  if (customElements.get('fs-sticky-atc')) return;
  class FsStickyAtc extends HTMLElement {
    connectedCallback() {
      this.mainBtn = document.querySelector('.product-form__submit');
      this.addBtn = this.querySelector('[data-add]');
      this.priceEl = this.querySelector('[data-price]');
      if (!this.mainBtn || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      this.observer = new IntersectionObserver(([entry]) => {
        const below = entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', !entry.isIntersecting && below);
        if (!entry.isIntersecting) this.sync();
      });
      this.observer.observe(this.mainBtn);
      this.addBtn.addEventListener('click', () => {
        this.sync();
        if (this.mainBtn.disabled) {
          this.mainBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.mainBtn.click();
      });
      document.addEventListener('change', (e) => {
        if (e.target.closest('variant-selects, variant-radios, product-info')) setTimeout(() => this.sync(), 400);
      });
    }

    disconnectedCallback() { this.observer?.disconnect(); }

    sync() {
      const mainPrice = document.querySelector('product-info .price-item--sale, product-info .price-item--regular');
      const salePrice = document.querySelector('product-info .price--on-sale .price-item--sale');
      const text = (salePrice || mainPrice)?.textContent.trim();
      if (text && this.priceEl) this.priceEl.textContent = text;
      const label = this.mainBtn.querySelector('span')?.textContent.trim();
      this.addBtn.disabled = false;
      if (this.mainBtn.disabled && label) {
        this.addBtn.textContent = label;
        this.addBtn.setAttribute('aria-disabled', 'true');
      } else {
        this.addBtn.textContent = this.dataset.label || this.addBtn.textContent;
        this.addBtn.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('fs-sticky-atc', FsStickyAtc);
})();

/* ---------- Fecha estimada de entrega ---------- */
(() => {
  if (customElements.get('fs-delivery')) return;
  const TZ = 'Europe/Madrid';

  // Fecha y hora actuales en Madrid, como objeto con año/mes/día/hora/min
  const madridNow = () => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (t) => Number(parts.find((p) => p.type === t).value);
    return { y: get('year'), m: get('month'), d: get('day'), h: get('hour'), min: get('minute') };
  };
  const iso = (dt) => `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;

  class FsDelivery extends HTMLElement {
    connectedCallback() {
      this.cutoff = Number(this.dataset.cutoff) || 14;
      this.minDays = Number(this.dataset.min) || 1;
      this.maxDays = Math.max(Number(this.dataset.max) || 3, this.minDays);
      this.holidays = new Set((this.dataset.holidays || '').split(',').map((s) => s.trim()).filter(Boolean));
      this.countdownEl = this.querySelector('[data-countdown-text]');
      this.rangeEl = this.querySelector('[data-range]');
      this.render();
      if (this.dataset.countdown === 'true') this.timer = setInterval(() => this.render(), 30000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    isWorkday(dt) {
      const wd = dt.getUTCDay();
      return wd !== 0 && wd !== 6 && !this.holidays.has(iso(dt));
    }

    addWorkdays(dt, n) {
      const d = new Date(dt);
      let added = 0;
      while (added < n) {
        d.setUTCDate(d.getUTCDate() + 1);
        if (this.isWorkday(d)) added++;
      }
      return d;
    }

    render() {
      const now = madridNow();
      const today = new Date(Date.UTC(now.y, now.m - 1, now.d));
      const beforeCutoff = now.h < this.cutoff;
      let ship = new Date(today);
      const shipsToday = this.isWorkday(today) && beforeCutoff;
      if (!shipsToday) {
        do { ship.setUTCDate(ship.getUTCDate() + 1); } while (!this.isWorkday(ship));
      }
      const from = this.addWorkdays(ship, this.minDays);
      const to = this.addWorkdays(ship, this.maxDays);
      const fmt = (dt) => new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(dt).replace(',', '');
      const before = this.dataset.before || 'Recíbelo entre el';
      const sameDay = iso(from) === iso(to);
      this.rangeEl.innerHTML = sameDay
        ? `Recíbelo el <strong>${fmt(from)}</strong>`
        : `${before} <strong>${fmt(from)}</strong> y el <strong>${fmt(to)}</strong>`;

      if (this.dataset.countdown === 'true' && shipsToday) {
        const minsLeft = (this.cutoff * 60) - (now.h * 60 + now.min);
        const h = Math.floor(minsLeft / 60);
        const m = minsLeft % 60;
        const left = h > 0 ? (m > 0 ? `${h} h ${m} min` : `${h} h`) : `${m} min`;
        this.countdownEl.innerHTML = `Pídelo en las próximas <strong>${left}</strong> y sale hoy`;
        this.countdownEl.hidden = false;
      } else {
        this.countdownEl.hidden = true;
      }
    }
  }
  customElements.define('fs-delivery', FsDelivery);
})();

/* ---------- Ventana de bienvenida con descuento ---------- */
(() => {
  if (customElements.get('fs-popup')) return;
  const KEY = 'eterna-popup-cerrado';
  const store = {
    get() { try { return Number(localStorage.getItem(KEY)) || 0; } catch (e) { return 0; } },
    set(v) { try { localStorage.setItem(KEY, String(v)); } catch (e) { /* sin almacenamiento */ } },
  };

  class FsPopup extends HTMLElement {
    connectedCallback() {
      this.box = this.querySelector('.fs-popup__box');
      this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelector('[data-copy]')?.addEventListener('click', (e) => this.copy(e.currentTarget));
      this.onKey = (e) => {
        if (this.hidden) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      };
      document.addEventListener('keydown', this.onKey);

      const success = this.querySelector('[data-success]');
      const inEditor = window.Shopify && window.Shopify.designMode;
      if (success) { this.open(); store.set(Date.now() + 365 * 864e5); return; }
      if (inEditor) {
        document.addEventListener('shopify:section:select', (e) => { if (this.closest('.shopify-section') === e.target) this.open(); });
        document.addEventListener('shopify:section:deselect', (e) => { if (this.closest('.shopify-section') === e.target) this.close(true); });
        return;
      }
      if (this.dataset.enabled !== 'true') return;
      if (store.get() > Date.now()) return;
      this.timer = setTimeout(() => this.open(), Number(this.dataset.delay) || 6000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKey);
    }

    focusables() {
      return Array.from(this.box.querySelectorAll('a[href], button, input, [tabindex]:not([tabindex="-1"])')).filter((el) => !el.disabled && el.offsetParent !== null);
    }

    trap(e) {
      const f = this.focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    open() {
      this.lastFocus = document.activeElement;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      document.body.classList.add('fs-popup-open');
      const input = this.querySelector('input[type="email"]');
      setTimeout(() => (input || this.querySelector('.fs-popup__close'))?.focus(), 50);
    }

    close(fromEditor = false) {
      this.classList.remove('is-open');
      this.hidden = true;
      document.body.classList.remove('fs-popup-open');
      if (!fromEditor && !(window.Shopify && window.Shopify.designMode)) {
        const days = Number(this.dataset.days) || 14;
        if (store.get() < Date.now()) store.set(Date.now() + days * 864e5);
      }
      this.lastFocus?.focus?.();
    }

    copy(btn) {
      const code = this.querySelector('[data-code]')?.textContent.trim();
      if (!code) return;
      const done = () => { btn.textContent = '¡Copiado!'; setTimeout(() => { btn.textContent = 'Copiar'; }, 2000); };
      if (navigator.clipboard) navigator.clipboard.writeText(code).then(done).catch(() => {});
    }
  }
  customElements.define('fs-popup', FsPopup);
})();

/* ---------- Avísame cuando vuelva (talla agotada) ---------- */
(() => {
  if (customElements.get('fs-notify')) return;
  class FsNotify extends HTMLElement {
    connectedCallback() {
      const raw = this.querySelector('[data-variants]')?.textContent.trim().split('|') || [];
      try {
        const ids = JSON.parse(raw[0]); const titles = JSON.parse(raw[1]); const avail = JSON.parse(raw[2]);
        this.variants = {};
        ids.forEach((id, i) => { this.variants[String(id)] = { title: titles[i], available: avail[i] }; });
      } catch (e) { this.variants = {}; }

      // Tras enviar el formulario, mostrar el mensaje de confirmación aunque la talla cambie
      if (this.querySelector('[data-done]')) { this.show(true); this.scrollIntoView({ block: 'center' }); return; }

      this.show(this.dataset.available !== 'true');
      this.form = this.closest('product-info') || document;
      this.onChange = () => setTimeout(() => this.sync(), 50);
      document.addEventListener('change', (e) => { if (e.target.closest('variant-selects, variant-radios')) this.onChange(); });
      if (window.subscribe && window.PUB_SUB_EVENTS) {
        this.unsub = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (ev) => {
          const v = ev?.data?.variant;
          if (v) this.apply(String(v.id), v.title, v.available); else this.sync();
        });
      }
      const idInput = document.querySelector('product-form form input[name="id"]');
      if (idInput && 'MutationObserver' in window) {
        this.mo = new MutationObserver(() => this.sync());
        this.mo.observe(idInput, { attributes: true, attributeFilter: ['value', 'disabled'] });
      }
    }

    disconnectedCallback() { this.unsub?.(); this.mo?.disconnect(); }

    sync() {
      const idInput = document.querySelector('product-form form input[name="id"]');
      const id = idInput ? String(idInput.value) : '';
      const v = this.variants[id];
      if (v) this.apply(id, v.title, v.available);
      else {
        const btn = document.querySelector('.product-form__submit');
        this.show(!!btn && btn.disabled);
      }
    }

    apply(id, title, available) {
      this.show(!available);
      this.querySelectorAll('[data-variant-name]').forEach((el) => { el.textContent = title; });
      const input = this.querySelector('[data-variant-input]');
      if (input) input.value = title;
      const body = this.querySelector('[data-body-input]');
      if (body) body.value = 'Aviso de vuelta al stock · ' + title + ' (variante ' + id + ')';
    }

    show(on) { this.classList.toggle('is-visible', on); }
  }
  customElements.define('fs-notify', FsNotify);
})();
